import { type CSSProperties } from "react";

/**
 * Bulk-select chrome shared by cockpit panels (Sprint 5v). The action bar
 * replaces a panel's header while rows are selected; the checkbox is the
 * hover-revealed per-row control.
 */

interface BulkActionBarProps {
  count: number;
  onMove: () => void;
  onArchive: () => void;
  onCancel: () => void;
}

export function BulkActionBar({ count, onMove, onArchive, onCancel }: BulkActionBarProps) {
  const bar: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
    padding: "4px 2px",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
  };

  const linkBtn = (variant?: "danger"): CSSProperties => ({
    border: "none",
    background: "transparent",
    color: variant === "danger" ? "var(--c-red-600)" : "var(--color-text-info)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    padding: 0,
    fontFamily: "var(--font-sans)",
  });

  return (
    <div style={bar} role="toolbar" aria-label="Bulk actions">
      <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>{count} selected</span>
      <span style={{ color: "var(--color-border-secondary)" }}>·</span>
      <button type="button" style={linkBtn()} onClick={onMove}>Move</button>
      <button type="button" style={linkBtn("danger")} onClick={onArchive}>Archive</button>
      <button
        type="button"
        style={{ ...linkBtn(), color: "var(--color-text-tertiary)", marginLeft: "auto" }}
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

interface BulkCheckboxProps {
  checked: boolean;
  /** True keeps the box rendered (selection active); else it only shows on row hover. */
  visible: boolean;
  onToggle: (e: { shiftKey?: boolean }) => void;
  label?: string;
}

/**
 * Hover-revealed row checkbox. The wrapper is `kuvio-bulk-checkbox`; panel rows
 * set opacity:0 by default and 1 on `.kuvio-panel-row:hover` / when `visible`.
 */
export function BulkCheckbox({ checked, visible, onToggle, label }: BulkCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label ?? "Select row"}
      className="kuvio-bulk-checkbox"
      data-visible={visible || checked ? "true" : "false"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle({ shiftKey: e.shiftKey });
      }}
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: checked
          ? "1px solid var(--color-text-info)"
          : "1px solid var(--color-border-secondary)",
        borderRadius: 4,
        background: checked ? "var(--color-text-info)" : "var(--color-background-primary)",
        color: "#fff",
        cursor: "pointer",
        padding: 0,
      }}
    >
      {checked && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />}
    </button>
  );
}
